/**
 * [M4-11] contacts + vCard + iMIP contracts (tasks/M4-CONTRACTS.md §2 contacts/calendar_invites · §4 Contacts/iMIP)
 * ไฟล์ใหม่ของ M4-11 — ห้ามแก้ mail.ts (freeze) · งานอื่นเพิ่มไฟล์ใหม่ + บรรทัด export ของตัวเองใน index.ts
 */
import { z } from "zod";

export const ContactSource = z.enum(["personal", "directory", "auto"]);
export type ContactSource = z.infer<typeof ContactSource>;

/** แถว contact — directory เป็น derived view: id=null read_only=true (แก้/ลบไม่ได้ ไม่มีแถวจริง) */
export const Contact = z.object({
  id: z.uuid().nullable(),
  name: z.string(),
  emails: z.array(z.string()),
  phones: z.array(z.string()),
  org: z.string().nullable(),
  notes: z.string().nullable(),
  source: ContactSource,
  merged_into: z.uuid().nullable(),
  /** ที่อยู่ของ directory row (mail account หลัก หรือ primary email ของ staff user) — null เมื่อไม่ใช่ directory */
  directory_email: z.string().nullable(),
  read_only: z.boolean(),
});
export type Contact = z.infer<typeof Contact>;

export const ContactCreate = z.object({
  name: z.string().trim().min(1).max(200),
  emails: z.array(z.email()).max(10).default([]),
  phones: z.array(z.string().trim().min(3).max(30)).max(10).default([]),
  org: z.string().trim().max(200).optional(),
  notes: z.string().max(2000).optional(),
});
export type ContactCreate = z.infer<typeof ContactCreate>;

export const ContactPatch = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    emails: z.array(z.email()).max(10).optional(),
    phones: z.array(z.string().trim().min(3).max(30)).max(10).optional(),
    org: z.string().trim().max(200).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: "ต้องส่งฟิลด์อย่างน้อยหนึ่งฟิลด์" });
export type ContactPatch = z.infer<typeof ContactPatch>;

export const ContactList = z.object({ items: z.array(Contact) });
export type ContactList = z.infer<typeof ContactList>;

export const ContactAutocompleteQuery = z.object({ q: z.string().trim().max(200) });
export type ContactAutocompleteQuery = z.infer<typeof ContactAutocompleteQuery>;

/** ranking ความถี่ล่าสุด (นับจาก mail_messages to/from) · จำกัด 20 แถว */
export const ContactAutocompleteItem = z.object({
  name: z.string().nullable(),
  email: z.string(),
  source: ContactSource,
  score: z.number().int(),
  last_at: z.string().nullable(),
});
export type ContactAutocompleteItem = z.infer<typeof ContactAutocompleteItem>;

export const ContactAutocompleteResponse = z.object({ items: z.array(ContactAutocompleteItem) });
export type ContactAutocompleteResponse = z.infer<typeof ContactAutocompleteResponse>;

export const VCardImportInput = z.object({ vcard: z.string().min(1).max(1_000_000) });
export type VCardImportInput = z.infer<typeof VCardImportInput>;

/** merge proposal — email ชน contact ที่มีอยู่: เสนอเท่านั้น ไม่ merge เอง (R3) */
export const VCardMergeProposal = z.object({
  existing_contact_id: z.uuid(),
  existing_name: z.string(),
  incoming: ContactCreate,
});
export type VCardMergeProposal = z.infer<typeof VCardMergeProposal>;

export const VCardImportResponse = z.object({
  created: z.array(Contact),
  proposals: z.array(VCardMergeProposal),
});
export type VCardImportResponse = z.infer<typeof VCardImportResponse>;

// ---------------------------------------------------------------------------
// iMIP (calendar_invites)
// ---------------------------------------------------------------------------

export const InviteMethod = z.enum(["REQUEST", "REPLY", "CANCEL", "COUNTER"]);
export type InviteMethod = z.infer<typeof InviteMethod>;

export const InviteAttendee = z.object({ email: z.string(), name: z.string().nullable(), status: z.string().nullable() });
export type InviteAttendee = z.infer<typeof InviteAttendee>;

/** การ์ดนัดจาก text/calendar — การ์ดเดียวต่อ uid (dedupe ตามล่าสุด) · CANCEL mark ยกเลิก (แสดง แต่ rsvp ไม่ได้) */
export const CalendarInviteCard = z.object({
  id: z.uuid(),
  message_id: z.uuid(),
  thread_id: z.uuid(),
  uid: z.string(),
  method: InviteMethod,
  summary: z.string().nullable(),
  starts_at: z.string().nullable(),
  ends_at: z.string().nullable(),
  tz: z.string().nullable(),
  location: z.string().nullable(),
  organizer: z.object({ email: z.string(), name: z.string().nullable() }).nullable(),
  attendees: z.array(InviteAttendee),
  my_status: z.enum(["accepted", "declined", "tentative"]).nullable(),
  recurrence_text: z.string().nullable(),
  canceled: z.boolean(),
  can_rsvp: z.boolean(),
  /** path ดาวน์โหลด .ics (หลัก) */
  ics_path: z.string(),
  /** ลิงก์เสริม "เพิ่มลง Google Calendar" — ใช้ download .ics เป็นหลัก ไม่พึ่งภายนอก */
  google_calendar_url: z.string(),
});
export type CalendarInviteCard = z.infer<typeof CalendarInviteCard>;

export const InviteListQuery = z.object({
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export type InviteListQuery = z.infer<typeof InviteListQuery>;

export const InviteListResponse = z.object({ items: z.array(CalendarInviteCard) });
export type InviteListResponse = z.infer<typeof InviteListResponse>;

export const InviteRsvpInput = z.object({ status: z.enum(["accepted", "declined", "tentative"]) });
export type InviteRsvpInput = z.infer<typeof InviteRsvpInput>;

export const InviteRsvpResponse = z.object({
  invite_id: z.uuid(),
  my_status: z.enum(["accepted", "declined", "tentative"]),
  /** false = กดสถานะเดิมซ้ำใน 1 นาที (ไม่สแปม REPLY) */
  sent: z.boolean(),
  outbox_message_id: z.string().nullable(),
});
export type InviteRsvpResponse = z.infer<typeof InviteRsvpResponse>;
